import { useState, useMemo, useEffect, useCallback } from 'react';
import { TrackingEvent } from '../../common/AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../common/AnalyticsContext/useAnalytics';
import { SLOWEST_USER_INTERACTION_DEBOUNCE } from '../../common/constants';
import { useDebouncedValue } from '../../common/hooks/useDebounceValue';
import { useRouter } from '../../common/hooks/useRouter';
import { api3Call } from '../../common/schema/api3/client';
import * as api3 from '../../common/schema/api3/endpoints';
import { HealthInterest } from '../../common/schema/api3/types/Api3Braze';
import { useFetch } from '../../common/services/NetworkService';
import { useBraze } from '../../common/vendors/braze/useBraze';

interface Data {
  [key: string]: boolean;
}

interface InterestAPI {
  name: string;
  subscribed: boolean;
}

export const useHealthInterests = (options?: {
  deferredSave?: boolean;
  skip?: boolean;
}): {
  userHealthInterests: HealthInterest[];
  changeHealthInterest: (interest: string, subscription: boolean) => void;
  updateHealthInterests: () => void;
  unSavedChanges: Data;
  loading: boolean;
  saving: boolean;
  error: boolean;
} => {
  const deferredSave = options?.deferredSave;
  const skip = options?.skip;
  const [userHealthInterests, setUserHealthInterests] = useState<HealthInterest[]>([]);
  const [unSavedChanges, setUnSavedChanges] = useState<Data>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const debouncedChanges = useDebouncedValue(unSavedChanges, SLOWEST_USER_INTERACTION_DEBOUNCE);

  const fetch = useFetch();
  const braze = useBraze();
  const router = useRouter();
  const { trackEvent } = useAnalytics();

  const pageUrl = router?.asPath;
  const bookingPageRegex = /\/appointment\/(book|book_mobile|book_iphone|book_android|book_webplugin|book_widget|book_practice)\//g;
  const isBookingPage = bookingPageRegex.test(pageUrl ?? '');

  useMemo(() => {
    if (braze && !skip) {
      setLoading(true);

      braze.getUser().getUserId(externalId => {
        if (externalId) {
          api3Call(fetch, api3.braze.getUserHealthInterests())
            .then(result => {
              setLoading(false);
              setError(false);

              setUserHealthInterests(result.health_interests);
            })
            .catch(error => {
              console.error('Could not fetch health interests', error);
              setLoading(false);
              setError(true);
            });
        } else {
          setLoading(false);
          setError(true);
        }
      });
    } else {
      setLoading(false);
    }
  }, [braze, fetch, skip]);

  const changeHealthInterest = (interest: string, subscription: boolean) => {
    const foundInterest = userHealthInterests.find(item => item.name === interest);
    if (!foundInterest) return;

    const filteredInterest = userHealthInterests.filter(item => item.name !== interest);

    setUserHealthInterests([...filteredInterest, { ...foundInterest, subscribed: subscription }]);

    if (unSavedChanges[interest] !== undefined && unSavedChanges[interest] === !subscription) {
      const tempHealthInterests = { ...unSavedChanges };
      delete tempHealthInterests[interest];
      setUnSavedChanges(tempHealthInterests);
    } else {
      setUnSavedChanges({ ...unSavedChanges, [interest]: subscription });
    }
  };

  const saveHealthInterests = useCallback(
    (interests: InterestAPI[]) => api3Call(fetch, api3.braze.saveUserHealthInterests(interests)),
    [fetch],
  );

  const updateHealthInterests = useCallback(() => {
    setSaving(true);
    setError(false);

    const requestData: InterestAPI[] = [];

    for (const key in unSavedChanges) {
      requestData.push({ name: key, subscribed: unSavedChanges[key] ?? false });
    }

    if (requestData.length > 0) {
      saveHealthInterests(requestData)
        .then(() => {
          setUnSavedChanges({});
          setSaving(false);
        })
        .then(() => {
          if (isBookingPage) {
            const subscribedInterest = userHealthInterests.filter(item => item.subscribed);

            trackEvent(TrackingEvent.HEALTH_INTERESTS_INTERACTED, {
              bookingData: {
                healthInterests: {
                  selectedCount:
                    subscribedInterest.length > 0 ? subscribedInterest.length.toString() : 'zero',
                },
              },
            });
          }
        })
        .catch(error => {
          setSaving(false);
          setError(true);
          console.error('could not save health interests', error);
        });
    } else {
      setSaving(false);
    }
  }, [saveHealthInterests, unSavedChanges, trackEvent, isBookingPage, userHealthInterests]);

  useEffect(() => {
    if (debouncedChanges && Object.keys(debouncedChanges).length > 0 && !deferredSave) {
      updateHealthInterests();
    }
  }, [debouncedChanges, deferredSave, updateHealthInterests]);

  return {
    userHealthInterests,
    changeHealthInterest,
    updateHealthInterests,
    unSavedChanges,
    loading,
    saving,
    error,
  };
};
